/**
 * Instrumentación temporal de rendimiento.
 *
 * No cambia el comportamiento de la aplicación: solo mide con
 * `performance.now()` y escribe en la consola. Está pensada para localizar
 * DÓNDE se van realmente los segundos en lugar de optimizar a ciegas.
 *
 * Se activa siempre en el panel (Guardar / Publicar) y en las páginas
 * públicas. Para el inventario completo de peticiones de red hay que añadir
 * `?ava-perf=1` a la URL, y `?ava-solo=1` para la prueba de aislamiento.
 */

const marks = new Map<string, number>()

function flag(name: string): boolean {
  if (typeof window === 'undefined') return false
  return new URLSearchParams(window.location.search).get(name) !== null
}

/** `?ava-perf=1`: además del cronómetro, vuelca el inventario de red. */
export function perfVerbose(): boolean {
  return flag('ava-perf')
}

/**
 * `?ava-solo=1`: prueba de aislamiento. La landing se renderiza SOLO con la
 * cabecera y el hero: sin formulario, sin vídeo, sin flotantes, sin garantías
 * y sin fondo. Si con eso sigue tardando, el problema no está en esos
 * componentes sino en la consulta inicial, el paquete o la infraestructura.
 */
export function perfSolo(): boolean {
  return flag('ava-solo')
}

export function mark(name: string): void {
  marks.set(name, performance.now())
}

/**
 * Cierra una medición y la imprime. `from` es una marca previa; si se omite se
 * mide desde el inicio de la navegación.
 */
export function measure(name: string, from?: string): number {
  const start = from ? (marks.get(from) ?? 0) : 0
  const ms = performance.now() - start
  console.log(`[AVA perf] ${name}: ${ms.toFixed(0)} ms`)
  return ms
}

/** Mide una promesa y devuelve su resultado sin alterarlo. */
export async function timed<T>(name: string, run: () => Promise<T>): Promise<T> {
  const start = performance.now()
  try {
    return await run()
  } finally {
    // Las etiquetas que ya traen su propio prefijo (por ejemplo «[AVA SAVE]»)
    // se imprimen tal cual para poder filtrarlas en la consola.
    const label = name.startsWith('[') ? name : `[AVA perf] ${name}`
    console.log(`${label}: ${(performance.now() - start).toFixed(0)} ms`)
  }
}

/**
 * Tiempos del navegador ANTES de que React exista: DNS, TLS, TTFB del HTML y
 * descarga del JS. Si el problema está aquí, optimizar React no sirve de nada.
 */
export function logNavigation(): void {
  if (typeof performance === 'undefined') return
  const nav = performance.getEntriesByType('navigation')[0] as
    | PerformanceNavigationTiming
    | undefined
  if (!nav) return
  console.log('[AVA perf] Navegación (antes de React)')
  console.table({
    'DNS (ms)': Math.round(nav.domainLookupEnd - nav.domainLookupStart),
    'TLS (ms)': Math.round(nav.connectEnd - nav.connectStart),
    'TTFB del HTML (ms)': Math.round(nav.responseStart - nav.startTime),
    'HTML descargado (ms)': Math.round(nav.responseEnd - nav.startTime),
    'DOM interactivo (ms)': Math.round(nav.domInteractive - nav.startTime),
    'DOM completo (ms)': Math.round(nav.domContentLoadedEventEnd - nav.startTime),
    'Carga total (ms)': Math.round(nav.loadEventEnd - nav.startTime),
  })
}

/**
 * Inventario real de TODAS las peticiones: cuándo empiezan, cuánto tardan y
 * cuánto pesan. Es el «waterfall» sin abrir la pestaña Red.
 */
export function logResources(label = 'Peticiones'): void {
  if (typeof performance === 'undefined') return
  const rows = (performance.getEntriesByType('resource') as PerformanceResourceTiming[])
    .map((entry) => ({
      recurso: entry.name.replace(/^https?:\/\//, '').slice(0, 78),
      tipo: entry.initiatorType,
      'inicio (ms)': Math.round(entry.startTime),
      'duración (ms)': Math.round(entry.duration),
      'KB': entry.transferSize ? Math.round(entry.transferSize / 1024) : 0,
    }))
    .sort((a, b) => b['duración (ms)'] - a['duración (ms)'])
  console.log(`[AVA perf] ${label}: ${rows.length} peticiones`)
  console.table(rows)
}

/** Mayor elemento pintado (LCP). Se registra en cuanto el navegador lo decide. */
export function observeLcp(): void {
  if (typeof PerformanceObserver === 'undefined') return
  try {
    const observer = new PerformanceObserver((list) => {
      const last = list.getEntries().at(-1) as
        | (PerformanceEntry & { element?: Element })
        | undefined
      if (!last) return
      console.log(
        `[AVA perf] LCP: ${last.startTime.toFixed(0)} ms`,
        last.element?.tagName ?? '',
      )
    })
    observer.observe({ type: 'largest-contentful-paint', buffered: true })
  } catch {
    // Navegador sin soporte: no es crítico.
  }
}

/** Resumen completo, disparado cuando la página termina de cargar. */
export function startPublicPerf(): void {
  if (typeof window === 'undefined') return
  observeLcp()
  const run = () => {
    logNavigation()
    const paint = performance
      .getEntriesByType('paint')
      .find((p) => p.name === 'first-contentful-paint')
    if (paint) {
      console.log(`[AVA perf] FCP: ${paint.startTime.toFixed(0)} ms`)
    }
    if (perfVerbose()) logResources()
  }
  if (document.readyState === 'complete') {
    window.setTimeout(run, 0)
  } else {
    window.addEventListener('load', () => window.setTimeout(run, 0), {
      once: true,
    })
  }
}
